
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/feature/Header';
import BottomNavigation from '../../components/feature/BottomNavigation';

interface CartItem {
  id: number;
  name: string;
  price: number;
  image: string;
  quantity: number;
}

interface DeliveryInfo {
  name: string;
  phone: string;
  address: string;
  city: string;
  notes: string;
}

export default function CheckoutPage() {
  const navigate = useNavigate();
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [deliveryInfo, setDeliveryInfo] = useState<DeliveryInfo>({
    name: '',
    phone: '',
    address: '',
    city: 'الرياض',
    notes: ''
  });
  const [paymentMethod, setPaymentMethod] = useState('mada');
  const [isProcessing, setIsProcessing] = useState(false);
  const [showLocationPicker, setShowLocationPicker] = useState(false);

  useEffect(() => {
    // تحميل السلة من localStorage
    const savedCart = localStorage.getItem('cartItems');
    if (savedCart) {
      const items = JSON.parse(savedCart);
      if (items.length === 0) {
        navigate('/cart');
        return;
      }
      setCartItems(items);
    } else {
      navigate('/cart');
    }
  }, [navigate]);

  const getTotalPrice = () => {
    return cartItems.reduce((total, item) => total + (item.price * item.quantity), 0);
  };

  const getTotalItems = () => {
    return cartItems.reduce((total, item) => total + item.quantity, 0);
  };

  const handleInputChange = (field: keyof DeliveryInfo, value: string) => {
    setDeliveryInfo(prev => ({ ...prev, [field]: value }));
  };

  const validateForm = () => {
    return deliveryInfo.name.trim() !== '' && 
           deliveryInfo.phone.trim() !== '' && 
           deliveryInfo.address.trim() !== '';
  };

  const handlePlaceOrder = async () => {
    if (!validateForm()) {
      alert('يرجى ملء جميع الحقول المطلوبة');
      return;
    }

    setIsProcessing(true);

    // محاكاة معالجة الطلب
    setTimeout(() => {
      // إنشاء رقم طلب عشوائي
      const orderNumber = Math.floor(Math.random() * 1000000) + 100000;
      
      // حفظ تفاصيل الطلب
      const orderData = {
        orderNumber,
        items: cartItems,
        deliveryInfo,
        paymentMethod,
        total: getTotalPrice() * 1.15,
        date: new Date().toISOString(),
        status: 'confirmed'
      };
      
      // حفظ الطلب في localStorage
      const existingOrders = JSON.parse(localStorage.getItem('orders') || '[]');
      localStorage.setItem('orders', JSON.stringify([orderData, ...existingOrders]));
      
      // مسح السلة
      localStorage.removeItem('cartItems');
      
      // الانتقال لصفحة تأكيد الطلب
      navigate('/order-confirmation', { 
        state: { orderNumber, total: orderData.total }
      });
      
      setIsProcessing(false);
    }, 2000);
  };

  const cities = [
    'الرياض', 'جدة', 'مكة المكرمة', 'المدينة المنورة', 'الدمام', 
    'الخبر', 'تبوك', 'بريدة', 'خميس مشيط', 'حائل'
  ];

  const paymentMethods = [
    { id: 'mada', name: 'مدى', icon: 'ri-bank-card-line' },
    { id: 'visa', name: 'فيزا', icon: 'ri-visa-line' },
    { id: 'mastercard', name: 'ماستركارد', icon: 'ri-mastercard-line' },
    { id: 'apple_pay', name: 'Apple Pay', icon: 'ri-apple-line' }
  ];

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <Header cartItemsCount={getTotalItems()} />
      
      {/* Location Picker Modal */}
      {showLocationPicker && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl p-6 w-full max-w-sm">
            <h3 className="text-lg font-bold text-[#333333] mb-4 text-center" dir="rtl">
              تحديد الموقع
            </h3>
            <p className="text-[#777777] text-center mb-6" dir="rtl">
              سيتم فتح خرائط جوجل لتحديد موقعك بدقة
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => {
                  // فتح خرائط جوجل (محاكاة)
                  setDeliveryInfo(prev => ({ 
                    ...prev, 
                    address: 'شارع الملك فهد، حي العليا، الرياض 12345' 
                  }));
                  setShowLocationPicker(false);
                }}
                className="flex-1 bg-[#D4AF37] text-white py-3 rounded-lg font-medium"
              >
                فتح الخرائط
              </button>
              <button
                onClick={() => setShowLocationPicker(false)}
                className="flex-1 bg-gray-200 text-[#333333] py-3 rounded-lg font-medium"
              >
                إلغاء
              </button>
            </div>
          </div>
        </div>
      )}
      
      <main className="pt-20 pb-32 px-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-6" dir="rtl">
          <h1 className="text-2xl font-bold text-[#333333]">
            إتمام الطلب
          </h1>
          <button
            onClick={() => navigate('/cart')}
            className="text-[#D4AF37] text-sm font-medium flex items-center"
          >
            <i className="ri-arrow-right-line ml-1"></i>
            العودة للسلة
          </button>
        </div>

        {/* Order Summary */}
        <div className="bg-white rounded-xl p-4 shadow-md mb-6">
          <h3 className="font-bold text-[#333333] mb-3 text-right" dir="rtl">
            ملخص الطلب ({getTotalItems()} منتج)
          </h3>
          
          <div className="space-y-3 mb-4">
            {cartItems.slice(0, 2).map((item) => (
              <div key={item.id} className="flex items-center gap-3" dir="rtl">
                <img
                  src={item.image}
                  alt={item.name}
                  className="w-12 h-12 object-cover rounded-lg"
                />
                <div className="flex-1">
                  <h4 className="text-sm font-medium text-[#333333] truncate">
                    {item.name}
                  </h4>
                  <p className="text-xs text-[#777777]">
                    {item.quantity} × {item.price} ر.س
                  </p>
                </div>
                <span className="text-sm font-bold text-[#4B2E05]">
                  {(item.price * item.quantity).toFixed(0)} ر.س
                </span>
              </div>
            ))}
            
            {cartItems.length > 2 && (
              <p className="text-sm text-[#777777] text-center">
                و {cartItems.length - 2} منتج آخر
              </p>
            )}
          </div>
          
          <div className="border-t pt-3">
            <div className="flex justify-between text-lg font-bold" dir="rtl">
              <span>المجموع الكلي</span>
              <span className="text-[#4B2E05]">
                {(getTotalPrice() * 1.15).toFixed(0)} ر.س
              </span>
            </div>
          </div>
        </div>

        {/* Delivery Information */}
        <div className="bg-white rounded-xl p-6 shadow-md mb-6">
          <h3 className="text-lg font-bold text-[#333333] mb-4 text-right" dir="rtl">
            معلومات التوصيل
          </h3>
          
          <div className="space-y-4" dir="rtl">
            <div>
              <label className="block text-sm font-medium text-[#333333] mb-2">
                الاسم الكامل *
              </label>
              <input
                type="text"
                value={deliveryInfo.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                className="w-full p-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent text-right"
                placeholder="أدخل اسمك الكامل"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-[#333333] mb-2">
                رقم الجوال *
              </label>
              <input
                type="tel"
                value={deliveryInfo.phone}
                onChange={(e) => handleInputChange('phone', e.target.value)}
                className="w-full p-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent text-right"
                placeholder="05xxxxxxxx"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-[#333333] mb-2">
                المدينة *
              </label>
              <select
                value={deliveryInfo.city}
                onChange={(e) => handleInputChange('city', e.target.value)}
                className="w-full p-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent text-right"
              >
                {cities.map((city) => (
                  <option key={city} value={city}>{city}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-[#333333] mb-2">
                العنوان التفصيلي *
              </label>
              <div className="relative">
                <textarea
                  value={deliveryInfo.address}
                  onChange={(e) => handleInputChange('address', e.target.value)}
                  className="w-full p-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent text-right resize-none"
                  rows={3}
                  placeholder="الحي، الشارع، رقم المبنى، رقم الشقة"
                />
                <button
                  onClick={() => setShowLocationPicker(true)}
                  className="absolute bottom-3 left-3 text-[#D4AF37] hover:text-[#B8941F]"
                >
                  <i className="ri-map-pin-line text-xl"></i>
                </button>
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-[#333333] mb-2">
                ملاحظات إضافية
              </label>
              <textarea
                value={deliveryInfo.notes}
                onChange={(e) => handleInputChange('notes', e.target.value)}
                className="w-full p-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent text-right resize-none"
                rows={2}
                placeholder="أي ملاحظات خاصة للتوصيل"
              />
            </div>
          </div>
        </div>

        {/* Payment Methods */}
        <div className="bg-white rounded-xl p-6 shadow-md mb-6">
          <h3 className="text-lg font-bold text-[#333333] mb-4 text-right" dir="rtl">
            طريقة الدفع
          </h3>
          
          <div className="space-y-3">
            {paymentMethods.map((method) => (
              <label
                key={method.id}
                className={`flex items-center p-4 border-2 rounded-lg cursor-pointer transition-colors ${
                  paymentMethod === method.id
                    ? 'border-[#D4AF37] bg-[#D4AF37]/5'
                    : 'border-gray-200 hover:border-[#D4AF37]/50'
                }`}
                dir="rtl"
              >
                <input
                  type="radio"
                  name="payment"
                  value={method.id}
                  checked={paymentMethod === method.id}
                  onChange={(e) => setPaymentMethod(e.target.value)}
                  className="sr-only"
                />
                <div className="flex items-center justify-between w-full">
                  <div className="flex items-center">
                    <i className={`${method.icon} text-2xl text-[#333333] ml-3`}></i>
                    <span className="font-medium text-[#333333]">{method.name}</span>
                  </div>
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                    paymentMethod === method.id
                      ? 'border-[#D4AF37] bg-[#D4AF37]'
                      : 'border-gray-300'
                  }`}>
                    {paymentMethod === method.id && (
                      <div className="w-2 h-2 bg-white rounded-full"></div>
                    )}
                  </div>
                </div>
              </label>
            ))}
          </div>
        </div>

        {/* Delivery Time */}
        <div className="bg-[#D4AF37]/10 rounded-xl p-4 mb-6">
          <div className="flex items-center gap-3" dir="rtl">
            <div className="w-10 h-10 bg-[#D4AF37] rounded-full flex items-center justify-center">
              <i className="ri-time-line text-white"></i>
            </div>
            <div>
              <h4 className="font-semibold text-[#333333] text-sm">
                وقت التوصيل المتوقع
              </h4>
              <p className="text-xs text-[#777777]">
                خلال 2-3 ساعات من تأكيد الطلب
              </p>
            </div>
          </div>
        </div>
      </main>

      {/* Fixed Bottom Action */}
      <div className="fixed bottom-16 left-0 right-0 bg-white border-t border-gray-200 p-4">
        <button
          onClick={handlePlaceOrder}
          disabled={!validateForm() || isProcessing}
          className={`w-full py-4 rounded-lg font-bold text-lg transition-colors ${
            validateForm() && !isProcessing
              ? 'bg-[#D4AF37] text-white hover:bg-[#B8941F]'
              : 'bg-gray-300 text-gray-500 cursor-not-allowed'
          }`}
        >
          {isProcessing ? (
            <div className="flex items-center justify-center">
              <i className="ri-loader-4-line animate-spin ml-2"></i>
              جاري معالجة الطلب...
            </div>
          ) : (
            `تأكيد الطلب - ${(getTotalPrice() * 1.15).toFixed(0)} ر.س`
          )}
        </button>
      </div>

      <BottomNavigation />
    </div>
  );
}
